import {
  PricingInformation,
  derivePricingForVariantGroup,
} from '../_components/pricing/PricingRange.client'

import type {
  ProductCatalogColorGroup,
  ProductCatalogProductColorOption,
} from '../../__generated__/graphql/catalog/graphql'

type RequiredColorGroupFields = Pick<ProductCatalogColorGroup, 'id'>
export type RequiredColorOptionFields = {
  color: {
    id: ProductCatalogProductColorOption['color']['id']
    hexCodes: ProductCatalogProductColorOption['color']['hexCodes']
    badge?: ProductCatalogProductColorOption['color']['badge']
    badgeExpiration?: ProductCatalogProductColorOption['color']['badgeExpiration']
    group: RequiredColorGroupFields | null
    swatchImage?: {
      url: NonNullable<ProductCatalogProductColorOption['color']['swatchImage']>['url']
    } | null
  }
}

type GroupedOptions = {
  group: RequiredColorGroupFields
  colorOptions: Array<RequiredColorOptionFields>
}

type MapOfGroupedOptions = Record<string, GroupedOptions>

export const groupColorOptionsByColorGroup = (
  colorOptions: RequiredColorOptionFields[]
): GroupedOptions[] => {
  const colorGroups: MapOfGroupedOptions = {}
  colorOptions.forEach(colorOption => {
    const colorGroup = colorOption.color.group?.id
      ? colorGroups[colorOption.color.group.id]
      : undefined

    // Check if the color ID already exists in the given color group
    const colorOptionExists = colorGroup?.colorOptions.find(
      co => co.color.id === colorOption.color.id
    )

    if (colorGroup) {
      if (!colorOptionExists) colorGroup.colorOptions.push(colorOption)
    } else if (colorOption.color.group?.id) {
      colorGroups[colorOption.color.group.id] = {
        group: colorOption.color.group,
        colorOptions: [colorOption],
      }
    }
  })
  return Object.values(colorGroups)
}

type PricingSupportedRequiredColorOptionFields = RequiredColorOptionFields & {
  outOfStock?: boolean
  compareAtPriceRange?: {
    min: {
      amount: number
    }
    max: {
      amount: number
    }
    minPercentageOff: number
    maxPercentageOff: number
  } | null
  priceRange: {
    currencyCode: string
    min: {
      amount: number
    }
    max: {
      amount: number
    }
  }
}

type PricingBucket = {
  id: string
  pricing: PricingInformation
  colorOptions: Array<PricingSupportedRequiredColorOptionFields>
}

type GroupedAndPricedOptions = {
  group: RequiredColorGroupFields
  colorOptions: Array<PricingSupportedRequiredColorOptionFields>
  pricingBuckets: Array<PricingBucket>
}

type MapOfGroupedAndPricedOptions = Record<string, GroupedAndPricedOptions>

export const groupColorOptionsByColorGroupAndPricing = (
  colorOptions: PricingSupportedRequiredColorOptionFields[]
): GroupedAndPricedOptions[] => {
  const pricedColorGroups: MapOfGroupedAndPricedOptions = {}
  colorOptions.forEach(colorOption => {
    const pricedColorGroup = colorOption.color.group?.id
      ? pricedColorGroups[colorOption.color.group.id]
      : undefined

    const colorPricing = derivePricingForVariantGroup(colorOption)

    if (pricedColorGroup) {
      const pricingBucket = pricedColorGroup.pricingBuckets.find(priceBucket =>
        isPricingInfoEqual(priceBucket.pricing, colorPricing)
      )
      pricedColorGroup.colorOptions.push(colorOption)
      if (pricingBucket) {
        pricingBucket.colorOptions.push(colorOption)
      } else {
        pricedColorGroup.pricingBuckets.push({
          id: hashPricingInfo(colorPricing),
          pricing: colorPricing,
          colorOptions: [colorOption],
        })

        pricedColorGroup.pricingBuckets.sort((thisBucket, thatBucket) => {
          return comparePricingInfo(thisBucket.pricing, thatBucket.pricing)
        })
      }
    } else if (colorOption.color.group?.id) {
      pricedColorGroups[colorOption.color.group.id] = {
        group: colorOption.color.group,
        colorOptions: [colorOption],
        pricingBuckets: [
          {
            id: hashPricingInfo(colorPricing),
            pricing: colorPricing,
            colorOptions: [colorOption],
          },
        ],
      }
    }
  })

  return Object.values(pricedColorGroups)
}

/*
 * Groups colors into two buckets for compact rendering:
 * - standard: full-price colors
 * - sale: colors with a compare-at price (discounted)
 * Colors without hex codes are omitted to avoid rendering empty swatches.
 */
export const groupColorOptionsBySalePrice = (
  colorOptions: PricingSupportedRequiredColorOptionFields[]
): GroupedAndPricedOptions[] => {
  const hasHexCodes = (colorOption: PricingSupportedRequiredColorOptionFields) =>
    Boolean(colorOption.color.hexCodes?.length)

  const standardColors = colorOptions.filter(
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    colorOption => colorOption && !colorOption.compareAtPriceRange && hasHexCodes(colorOption)
  )
  const onSaleColors = colorOptions.filter(
    colorOption => colorOption.compareAtPriceRange && hasHexCodes(colorOption)
  )

  const groups = [
    {
      group: {
        id: 'standard',
        displayValue: '',
      },
      colorOptions: standardColors,
      pricingBuckets: [],
    },
    {
      group: {
        id: 'sale',
        displayValue: 'sale',
      },
      colorOptions: onSaleColors,
      pricingBuckets: [],
    },
  ].filter(group => group.colorOptions.length > 0)

  return groups
}

function hashPricingInfo(thisPricing: PricingInformation) {
  return `${thisPricing.priceRange.min}-${thisPricing.priceRange.max}-${thisPricing.discountPriceRange?.min}-${thisPricing.discountPriceRange?.max}`
}

function isPricingInfoEqual(thisPricing: PricingInformation, thatPricing: PricingInformation) {
  return hashPricingInfo(thisPricing) === hashPricingInfo(thatPricing)
}

function comparePricingInfo(thisPricing: PricingInformation, thatPricing: PricingInformation) {
  if (thisPricing.discountPriceRange && !thatPricing.discountPriceRange) {
    return -1
  } else if (thisPricing.discountPriceRange && thatPricing.discountPriceRange) {
    return thatPricing.discountPriceRange.min - thisPricing.discountPriceRange.min
  } else {
    return thatPricing.priceRange.min - thisPricing.priceRange.min
  }
}
